import { useCallback, useRef, useState } from 'react'
import { ChevronDown, Edit3, Film, Play, RefreshCw, X } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { ContentLabel, StoryboardFrameData, VisualTreatment } from './types'
import {
  CONTENT_LABELS,
  CONTENT_LABEL_COLORS,
  VISUAL_TREATMENT_OPTIONS,
} from './types'
import { VideoThumbnail } from '../../repository/VideoThumbnail'

function getContentLabelStyle(label: string): { bg: string; text: string } {
  const match = CONTENT_LABELS.find(l => l.toLowerCase() === label.toLowerCase())
  if (match) return CONTENT_LABEL_COLORS[match]
  return CONTENT_LABEL_COLORS.Scene
}

function MediaCell({
  frame,
  onFrameClick,
  onFramePreview,
  onRegenerate,
  onRemove,
}: {
  frame: StoryboardFrameData
  onFrameClick?: () => void
  onFramePreview?: () => void
  onRegenerate?: () => void
  onRemove?: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [imageError, setImageError] = useState(false)

  const hasImage = frame.image && frame.image.length > 0 && !imageError
  const hasVideo = frame.videoUrl && frame.videoUrl.length > 0
  const hasSourceVideo = !!frame.sourceVideoId
  const hasMedia = hasImage || hasVideo || hasSourceVideo

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true)
    if (hasVideo) videoRef.current?.play().catch(() => {})
  }, [hasVideo])

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false)
    if (hasVideo) {
      const vid = videoRef.current
      if (vid) {
        vid.pause()
        vid.currentTime = 0
      }
    }
  }, [hasVideo])

  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-[10px] font-medium text-gray-500">Main</span>
      <div
        className="relative group aspect-[4/5] w-full rounded-md overflow-hidden bg-gray-50 cursor-pointer border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onFrameClick}
      >
        {hasVideo ? (
          <>
            <video
              ref={videoRef}
              src={frame.videoUrl}
              poster={hasImage ? frame.image : undefined}
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            />
            {!isHovering && (
              <div className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center">
                <Play className="h-2.5 w-2.5 text-white fill-white ml-0.5" />
              </div>
            )}
          </>
        ) : hasSourceVideo ? (
          <VideoThumbnail
            videoId={frame.sourceVideoId!}
            videoStatus="ready"
            thumbnailUrl={hasImage ? frame.image : undefined}
            className="w-full h-full"
          />
        ) : hasImage ? (
          <img
            src={frame.image}
            alt={`Scene ${frame.timeStart}`}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 border border-dashed border-gray-200 rounded-md">
            <Film className="h-5 w-5 text-gray-300 mb-1" />
            <span className="text-[9px] text-gray-400">Select footage</span>
          </div>
        )}

        {/* Hover action icons */}
        {isHovering && (
          <div className="absolute top-1 right-1 flex items-center gap-0.5">
            {onRemove && (
              <button
                className="p-1 rounded bg-white/90 hover:bg-white shadow-sm"
                onClick={e => { e.stopPropagation(); onRemove() }}
                title="Remove"
              >
                <X className="h-3 w-3 text-gray-600" />
              </button>
            )}
            {onRegenerate && (
              <button
                className="p-1 rounded bg-white/90 hover:bg-white shadow-sm"
                onClick={e => { e.stopPropagation(); onRegenerate() }}
                title="Regenerate"
              >
                <RefreshCw className="h-3 w-3 text-gray-600" />
              </button>
            )}
            <button
              className="p-1 rounded bg-white/90 hover:bg-white shadow-sm"
              onClick={e => { e.stopPropagation(); onFrameClick?.() }}
              title="Edit footage"
            >
              <Edit3 className="h-3 w-3 text-gray-600" />
            </button>
          </div>
        )}
      </div>
      {hasMedia && (
        <button
          className="text-[11px] text-blue-600 hover:text-blue-700 hover:underline"
          onClick={onFramePreview ?? onFrameClick}
        >
          View Preview
        </button>
      )}
    </div>
  )
}

function ContentLabelDropdown({
  value,
  onChange,
}: {
  value: string
  onChange: (label: ContentLabel) => void
}) {
  const [open, setOpen] = useState(false)
  const style = getContentLabelStyle(value)
  const dropdownRef = useRef<HTMLDivElement>(null)

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold cursor-pointer',
          style.bg,
          style.text
        )}
      >
        {value}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px] max-h-[240px] overflow-y-auto">
            {CONTENT_LABELS.map(label => {
              const labelStyle = getContentLabelStyle(label)
              const isSelected = label.toLowerCase() === value.toLowerCase()
              return (
                <button
                  key={label}
                  onClick={() => {
                    onChange(label)
                    setOpen(false)
                  }}
                  className={cn(
                    'w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2',
                    isSelected && 'bg-gray-50'
                  )}
                >
                  {isSelected && <span className="text-gray-700">✓</span>}
                  <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold', labelStyle.bg, labelStyle.text)}>
                    {label}
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

interface AIGeneratedTableProps {
  frames: StoryboardFrameData[]
  className?: string
  onFrameClick?: (frame: StoryboardFrameData) => void
  onFramePreview?: (frame: StoryboardFrameData) => void
  onFrameRegenerate?: (frame: StoryboardFrameData) => void
  onFrameRemove?: (frame: StoryboardFrameData) => void
  onFrameUpdate?: (frameId: string, updates: Partial<StoryboardFrameData>) => void
}

export function AIGeneratedTable({
  frames,
  className,
  onFrameClick,
  onFramePreview,
  onFrameRegenerate,
  onFrameRemove,
  onFrameUpdate,
}: AIGeneratedTableProps) {
  if (frames.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Film className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-500">No generated scenes</p>
        <p className="text-xs text-gray-400 mt-1">Click &apos;Regenerate All&apos; to generate storyboard scenes</p>
      </div>
    )
  }

  return (
    <div className={cn('', className)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <tbody>
            {/* Content Row */}
            <tr className="border-b border-gray-100">
              <td className="py-2 pr-4 align-top w-24">
                <span className="text-xs font-medium text-gray-500">Content</span>
              </td>
              {frames.map(frame => (
                <td key={`content-${frame.id}`} className="py-2 px-2 align-top">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <ContentLabelDropdown
                      value={frame.label}
                      onChange={label => onFrameUpdate?.(frame.id, { label })}
                    />
                    <span className="text-gray-300">|</span>
                    <select
                      className="h-6 text-[11px] border border-gray-200 rounded bg-white text-gray-600 px-1.5 max-w-[140px] cursor-pointer"
                      value={frame.visualTreatment || ''}
                      onChange={e => {
                        const val = e.target.value as VisualTreatment
                        if (val) onFrameUpdate?.(frame.id, { visualTreatment: val })
                      }}
                    >
                      <option value="">Visual treatment...</option>
                      {VISUAL_TREATMENT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </td>
              ))}
            </tr>

            {/* Time Row */}
            <tr className="border-b border-gray-100">
              <td className="py-2 pr-4 align-middle w-24">
                <span className="text-xs font-medium text-gray-500">Time</span>
              </td>
              {frames.map(frame => (
                <td key={`time-${frame.id}`} className="py-2 px-2 align-middle">
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    {frame.timeStart} → {frame.timeEnd}
                  </span>
                </td>
              ))}
            </tr>

            {/* Media Row */}
            <tr className="border-b border-gray-100">
              <td className="py-3 pr-4 align-middle w-24">
                <span className="text-xs font-medium text-gray-500">Media</span>
              </td>
              {frames.map(frame => (
                <td key={`media-${frame.id}`} className="py-3 px-2 align-top">
                  <div className="w-[140px]">
                    <MediaCell
                      frame={frame}
                      onFrameClick={() => onFrameClick?.(frame)}
                      onFramePreview={() => onFramePreview?.(frame)}
                      onRegenerate={() => onFrameRegenerate?.(frame)}
                      onRemove={() => onFrameRemove?.(frame)}
                    />
                  </div>
                </td>
              ))}
            </tr>

            {/* Text Overlay Row (editable) */}
            <tr className="border-b border-gray-100">
              <td className="py-2 pr-4 align-top w-24">
                <span className="text-xs font-medium text-gray-500">Text overlay</span>
              </td>
              {frames.map(frame => (
                <td key={`text-${frame.id}`} className="py-2 px-2 align-top">
                  <textarea
                    className="w-full max-w-[160px] min-h-[48px] text-xs text-gray-700 leading-relaxed border border-gray-200 rounded-md px-2 py-1.5 resize-none focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 bg-white"
                    value={frame.textOverlay || ''}
                    onChange={e => onFrameUpdate?.(frame.id, { textOverlay: e.target.value })}
                    placeholder="Add text overlay..."
                    rows={2}
                  />
                </td>
              ))}
            </tr>

            {/* Audio Row (editable) */}
            <tr>
              <td className="py-2 pr-4 align-top w-24">
                <span className="text-xs font-medium text-gray-500">Audio</span>
              </td>
              {frames.map(frame => (
                <td key={`audio-${frame.id}`} className="py-2 px-2 align-top">
                  <textarea
                    className="w-full max-w-[160px] min-h-[48px] text-xs text-gray-600 italic leading-relaxed border border-gray-200 rounded-md px-2 py-1.5 resize-none focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 bg-white"
                    value={frame.audioText || ''}
                    onChange={e => onFrameUpdate?.(frame.id, { audioText: e.target.value })}
                    placeholder="Add audio text..."
                    rows={2}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
